import { createFileRoute, getRouteApi } from '@tanstack/react-router'
import { useVirtualizer } from '@tanstack/react-virtual'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box } from '../../../components/Box.tsx'
import { Alert, Button, Checkbox, Divider, List, Menu, MenuItem } from '../../../components/generic.tsx'
import { I18n } from '../../../components/I18n.tsx'
import { IconButton } from '../../../components/IconButton.tsx'
import { Restricted } from '../../../components/Restricted.tsx'
import { Stack } from '../../../components/Stack.tsx'
import { useFavoriteAssets, useUpdateFavoriteAssets } from '../../../data/assets.ts'
import { isAssetLocation, type Location, useLocations } from '../../../data/locations.tsx'
import { AlertCounts } from '../../../features/locations/alerts/AlertCounts.tsx'
import { FavoritesDisclaimer, LocationRow, LocationsTableHeader, useLocationSearch } from '../../../features/locations/LocationList.tsx'
import {
	useFilteredLocations,
	useGetLocationsForFilter,
	useSearchLocations,
	useSortLocations,
} from '../../../features/locations/locationUtils.ts'
import { AssetAlertCards, DeviceAlertCards } from '../../../features/panel/AlertsPanel.tsx'
import { AnimatedSwitcher } from '../../../features/panel/cards/AlertCard.tsx'
import { ids } from '../../../ids.ts'
import { useLocationFilter } from '../../../states.ts'

export const Route = createFileRoute('/_app/m/_mobile/locations')({
	component: RouteComponent,
})

const route = getRouteApi('/_app/m/_mobile/locations')

const rowHeightPx = 48
function RouteComponent() {
	const [isEditingFavorites, setIsEditingFavorites] = useState(false)
	const { data: favorites } = useFavoriteAssets()
	const { mutate: updateFavorites, isPending: isUpdateFavoritesPending } = useUpdateFavoriteAssets()
	const [selectedLocations, setSelectedLocations] = useState<Set<string>>(new Set())

	const search = useLocationSearch((s) => s.search)

	const { locationId } = route.useSearch({ select: ({ locationId }) => ({ locationId }) })
	const navigate = route.useNavigate()

	const { locations: filteredLocations } = useFilteredLocations()
	const { data: allLocations } = useLocations()
	const locations = isEditingFavorites ? allLocations : filteredLocations
	const selectedLocation = locations.find((l) => l.id === locationId)
	const searchedLocations = useSearchLocations(locations, search)
	const sortLocations = useSortLocations()

	const rows = useMemo(
		() => (search ? searchedLocations : searchedLocations.toSorted(sortLocations)),
		[search, searchedLocations, sortLocations],
	)

	const areAllLocationsFavorites = useMemo(
		() => locations.filter(isAssetLocation).every((r) => selectedLocations.has(r.assetVrn)),
		[locations, selectedLocations],
	)

	const scrollElementRef = useRef<HTMLDivElement>(null)
	const virtualizer = useVirtualizer({
		count: rows.length,
		getScrollElement: () => scrollElementRef.current,
		estimateSize: () => rowHeightPx,
		getItemKey: (index) => rows[index]?.id ?? index.toString(),
		overscan: 5,
	})
	const virtualItems = virtualizer.getVirtualItems()

	const onLocationSelect = useCallback(
		async (loc?: Location) => {
			await navigate({ to: '/m/locations', search: (prev) => ({ ...prev, locationId: loc?.id }) })
		},
		[navigate],
	)

	const onToggleFavorite = useCallback((location: Location) => {
		setSelectedLocations((prev) => {
			const next = new Set(prev)
			if (!isAssetLocation(location)) return prev
			const assetVrn = location.assetVrn
			next.has(assetVrn) ? next.delete(assetVrn) : next.add(assetVrn)
			return next
		})
	}, [])

	const handleFavoriteAll = () => {
		if (!areAllLocationsFavorites) {
			return setSelectedLocations(new Set(locations.filter(isAssetLocation).map((r) => r.assetVrn)))
		}
		setSelectedLocations(new Set())
	}

	const handleApply = async () => {
		await updateFavorites({ vrns: [...selectedLocations] })
		setIsEditingFavorites(false)
	}

	useEffect(() => {
		if (!isEditingFavorites) return
		setSelectedLocations(new Set(favorites?.map((f) => f.assetVrn) ?? []))
	}, [isEditingFavorites, favorites])

	return (
		<Box flex col full>
			<Stack align='center' sx={{ boxShadow: 1, ...(!isEditingFavorites ? { zIndex: 201 } : {}) }}>
				<AnimatedSwitcher
					toggle={isEditingFavorites}
					first={
						<Stack align='center' justify='space-between' p={1} sx={{ width: '100vw' }}>
							<AlertCounts />
							<Restricted feature='protections'>
								<LocationHamburgerMenu onEditFavorites={() => setIsEditingFavorites(true)} />
							</Restricted>
						</Stack>
					}
					second={
						<Box sx={{ width: '100vw', bgcolor: (t) => t.palette.background.paper2 }}>
							<EditFavoritesDisclaimer />
							<Divider />
							<Stack justify='space-between' p={2}>
								<Button variant='text' color='primary' size='small' onClick={handleFavoriteAll}>
									<I18n i18nKey={($) => (!areAllLocationsFavorites ? $.selectAll : $.deselectAll)} />
								</Button>
								<Button variant='text' color='primary' size='small' onClick={() => setIsEditingFavorites(false)}>
									<I18n i18nKey={($) => $.cancel} />
								</Button>
								<Button color='primary' size='small' onClick={handleApply} disabled={isUpdateFavoritesPending}>
									<I18n i18nKey={($) => $.apply} />
								</Button>
							</Stack>
						</Box>
					}
				/>
			</Stack>
			<LocationsTableHeader />
			{!isEditingFavorites && <FavoritesDisclaimer />}
			<Box data-ta={ids.mobile.locationList} ref={scrollElementRef} sx={{ overflowY: 'auto', overflowX: 'hidden', flex: 1, minHeight: 0 }}>
				<List sx={{ position: 'relative', height: virtualizer.getTotalSize(), p: 0 }}>
					{virtualItems.map((virtualItem) => {
						const location = rows[virtualItem.index]
						if (!location) return null
						return (
							<MobileRow
								key={virtualItem.key}
								location={location}
								isExpanded={selectedLocation?.id === location.id}
								onSelect={onLocationSelect}
								isEditing={isEditingFavorites}
								isChecked={isAssetLocation(location) && selectedLocations.has(location.assetVrn)}
								onToggle={onToggleFavorite}
								index={virtualItem.index}
								start={virtualItem.start}
								measureRef={virtualizer.measureElement}
							/>
						)
					})}
				</List>
			</Box>
		</Box>
	)
}

const MobileRow = memo(
	({
		location,
		isExpanded,
		onSelect,
		start,
		index,
		isEditing,
		isChecked,
		onToggle,
		measureRef,
	}: {
		location: Location
		isExpanded: boolean
		onSelect: (loc?: Location) => Promise<void>
		start: number
		index: number
		isEditing: boolean
		isChecked: boolean
		onToggle: (loc: Location) => void
		measureRef: (el: Element | null) => void
	}) => {
		const handleExpandedChange = useCallback(
			(exp: boolean) => {
				void onSelect(exp ? location : undefined)
			},
			[location, onSelect],
		)
		const first =
			isEditing && isAssetLocation(location) ? (
				<Checkbox checked={isChecked} size='large' onChange={() => onToggle(location)} disabled={!isAssetLocation(location)} />
			) : undefined

		return (
			<LocationRow
				ref={measureRef}
				data-index={index}
				location={location}
				selected={isExpanded}
				expanded={isExpanded}
				onExpandedChange={handleExpandedChange}
				first={first}
				expandedContent={<LocationDetails location={location} />}
				sx={{ position: 'absolute', top: 0, left: 0, width: '100%', transform: `translateY(${start}px)` }}
			/>
		)
	},
)

const LocationDetails = memo(function LocationDetails({ location }: { location: Location }) {
	return (
		<>
			{location.type === 'asset' && <AssetAlertCards asset={location} />}
			{location.type === 'device' && <DeviceAlertCards device={location} />}
		</>
	)
})

const LocationHamburgerMenu = ({ onEditFavorites }: { onEditFavorites: () => void }) => {
	const { t } = useTranslation()
	const { locationId } = route.useSearch({ select: ({ locationId }) => ({ locationId }) })
	const navigate = route.useNavigate()
	const { isPending: isLocationsPending } = useLocations()
	const { isPending: isFavoritesPending } = useFavoriteAssets()
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
	const getLocationsForFilter = useGetLocationsForFilter()
	const filter = useLocationFilter((s) => s.filter)
	const setFilter = useLocationFilter((s) => s.setFilter)

	const handleSelect = async (value: 'all' | 'favorites') => {
		setFilter(value)
		const isLocationInFilter = locationId != null && getLocationsForFilter(value).some((loc) => loc.id === locationId)
		await navigate({
			search: (prev) => ({ ...prev, locationId: isLocationInFilter ? prev.locationId : undefined }),
			replace: true,
		})
		setAnchorEl(null)
	}

	const isPending = isLocationsPending || isFavoritesPending

	return (
		<>
			<IconButton
				icon='Bars'
				label={t(($) => $.locations.locationsList.openMenu)}
				size='small'
				onClick={(e) => setAnchorEl(e.currentTarget)}
			/>
			<Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} slotProps={{ list: { dense: true } }}>
				<MenuItem selected={!filter || filter === 'all'} onClick={() => void handleSelect('all')}>
					<I18n i18nKey={($) => $.locations.locationsList.menu.viewAllLocations} />
				</MenuItem>
				<MenuItem selected={filter === 'favorites'} onClick={() => void handleSelect('favorites')}>
					<I18n i18nKey={($) => $.locations.locationsList.menu.viewFavorites} />
				</MenuItem>
				<Divider />
				<MenuItem
					onClick={() => {
						onEditFavorites()
						setAnchorEl(null)
					}}
					disabled={isPending}
				>
					<I18n i18nKey={($) => $.locations.locationsList.editFavorites} />
				</MenuItem>
			</Menu>
		</>
	)
}

const EditFavoritesDisclaimer = () => (
	<Alert severity='caution'>
		<I18n i18nKey={($) => $.locations.locationsList.editFavoritesDisclaimer} />
	</Alert>
)
